import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PublicShell, Loading } from "@/components/shared";
import { AudioPlayer } from "@/components/podcast/AudioPlayer";
import { ShowCover, SHOW_NAME, SHOW_HOST, fmtDate, fmtMinutes, isoDuration } from "@/components/podcast/common";
import { ShareButtons } from "@/components/ShareButtons";
import { PodcastSubscribe } from "@/pages/Podcast";
import { useHead } from "@/lib/useHead";
import api from "@/lib/api";
import NotFound from "@/pages/NotFound";

function EpisodeNav({ previous, next }) {
  if (!previous && !next) return null;
  return (
    <nav className="mt-10 grid gap-3 border-t border-border pt-6 sm:grid-cols-2" data-testid="episode-nav">
      {previous ? (
        <Link to={`/podcast/${previous.slug}`} className="group rounded-[10px] border border-border bg-card p-4" data-testid="episode-prev">
          <span className="kicker inline-flex items-center gap-1 text-[11px] text-accent"><ArrowLeft className="h-3 w-3" /> Previous</span>
          <span className="mt-1 block font-heading font-semibold text-ink group-hover:text-accent">{previous.title}</span>
        </Link>
      ) : <span />}
      {next && (
        <Link to={`/podcast/${next.slug}`} className="group rounded-[10px] border border-border bg-card p-4 sm:text-right" data-testid="episode-next">
          <span className="kicker inline-flex items-center gap-1 text-[11px] text-accent">Next <ArrowRight className="h-3 w-3" /></span>
          <span className="mt-1 block font-heading font-semibold text-ink group-hover:text-accent">{next.title}</span>
        </Link>
      )}
    </nav>
  );
}

export default function PodcastEpisode() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useQuery({ queryKey: ["podcast-episode", slug], retry: false,
    queryFn: async () => (await api.get(`/podcast/episodes/${slug}`)).data });
  const ep = data?.episode;
  const url = `${window.location.origin}/podcast/${slug}`;
  const image = ep?.image_url || data?.show?.image_url || undefined;
  useHead({
    title: ep ? `${ep.title} — ${SHOW_NAME}` : "", description: ep ? (ep.description_text || "").slice(0, 200) : "",
    image, url, type: "article",
    jsonLd: ep && { "@context": "https://schema.org", "@type": "PodcastEpisode", name: ep.title, url,
      datePublished: ep.published_at, duration: isoDuration(ep.duration_seconds), episodeNumber: ep.episode_number || undefined,
      description: (ep.description_text || "").slice(0, 300), image,
      associatedMedia: { "@type": "MediaObject", contentUrl: ep.audio_url, encodingFormat: ep.audio_type },
      partOfSeries: { "@type": "PodcastSeries", name: SHOW_NAME, url: `${window.location.origin}/podcast` } },
  });

  if (isLoading) return <PublicShell><Loading /></PublicShell>;
  if (isError || !ep) return <NotFound />;

  return (
    <PublicShell>
      <div className="podcast-scope bg-background">
        <article className="mx-auto max-w-[760px] px-5 py-12" data-testid="podcast-episode-page">
          <Link to="/podcast" className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-muted hover:text-ink" data-testid="episode-back"><ArrowLeft className="h-4 w-4" /> {SHOW_NAME}</Link>
          <div className="flex items-start gap-4">
            <ShowCover src={image} className="w-20 flex-shrink-0" testid="episode-cover" />
            <div className="min-w-0">
              <p className="kicker text-[11px] text-accent">{ep.episode_number ? `Episode ${ep.episode_number}` : "Episode"}<span className="text-muted"> · {fmtDate(ep.published_at)}{ep.duration_seconds ? ` · ${fmtMinutes(ep.duration_seconds)}` : ""}</span></p>
              <h1 className="mt-1.5 font-heading text-[clamp(1.6rem,5vw,2.25rem)] font-extrabold leading-tight text-ink" data-testid="episode-title">{ep.title}</h1>
              <p className="mt-1 text-sm text-muted">Hosted by {SHOW_HOST}</p>
            </div>
          </div>
          <div className="mt-6"><AudioPlayer src={ep.audio_url} durationHint={ep.duration_seconds} testid="episode-player" /></div>
          <div className="mt-6"><ShareButtons url={url} title={`${ep.title} — ${SHOW_NAME}`} testid="episode-share" /></div>
          <div className="podcast-notes mt-8 text-[16px] leading-[1.75] text-ink" data-testid="episode-notes" dangerouslySetInnerHTML={{ __html: ep.description_html }} />
          <div className="mt-10"><PodcastSubscribe /></div>
          <EpisodeNav previous={data.previous} next={data.next} />
        </article>
      </div>
    </PublicShell>
  );
}
